/**
 * Unit — a deployed tower occupying one slot. Engine-agnostic. Melee units
 * (range 0) block a span of the path and fight engaged enemies; ranged units
 * (range > 0) sit safely and fire at enemies within euclidean range (which is
 * how the layered side-view "rain fire on the lane below" works).
 */
import { DEFAULT_STATS, type Stats } from '@/battle/Stats';
import { Soldier } from '@/battle/entities/Soldier';
import type { TargetMode, UnitFamily } from '@/data/schema/common';

export class Unit {
  slotId = '';
  defId = '';
  family: UnitFamily = 'melee';
  x = 0;
  y = 0;

  tier = 0;
  maxTier = 0;
  /** Chosen specialization branch id, or null until the branch point is taken. */
  branchId: string | null = null;
  stats: Stats = DEFAULT_STATS;

  /** Melee only: the squad's bodies — each blocks/fights/falls individually.
   *  Empty for ranged units. The aggregate `hp`/`maxHp`/`dead` below are derived
   *  from these each tick (sum of living soldiers) for HUD readouts. */
  soldiers: Soldier[] = [];

  /** Melee only (derived): total HP across living soldiers and its max. On a
   *  squad reaching 0 living soldiers the unit reads as `dead`. Regen/respawn is
   *  per-soldier (see World.MELEE_REGEN_FRACTION_PER_S / MELEE_RESPAWN_S). */
  hp = 0;
  maxHp = 0;
  /** True while EVERY soldier has fallen (the line is briefly open here). */
  dead = false;
  /** Seconds until the next fallen soldier respawns (soonest in the squad). */
  respawnRemaining = 0;

  range = 0;
  attackCooldown = 0;
  targetMode: TargetMode = 'first';

  /** Total gold spent (deploy + upgrades) — basis for the sell refund. */
  goldSpent = 0;

  isMelee = false;
  isHero = false;
  blockSpan = 0;
  /** Per-soldier hold capacity: how many enemies ONE soldier stops at once.
   *  The squad's total capacity is `soldiers.length × blockCount`. */
  blockCount = 1;
  /** How many soldier bodies the squad fields at the current tier. */
  squadSize = 1;
  /** Path distance at which the squad physically blocks its PRIMARY lane (the
   *  nearest path). Used for soldier rendering and as the single-lane default. */
  blockDistance = 0;
  /** Primary lane (nearest path index) the squad stands on — for rendering. */
  blockLane = 0;
  /** Every lane this blocker physically covers (within reach of the slot) and the
   *  path distance it holds on each. On a single-path stage this is just lane 0;
   *  in a fork it's the near lane(s). The squad's hold CAPACITY is shared across
   *  these lanes (one soldier stops one enemy, whichever lane it's on). */
  blocks: { lane: number; distance: number }[] = [];
}
